import { useAppState } from "Context/StateContext";
import { useEventBus } from "Hooks/useEventBus";
import { BusEventPayloads } from "Types";
import { BusEventNames, TaskStatus } from "Util/enums";

import { useOperationDespatchContext } from "../../contexts/DespatchContext";

export const useDespatchPopupHandlers = () => {
  const {
    addNewStorageBoxToCrew,
    updateCrewTaskValueByTaskId,
    addTaskComment,
    deleteTaskComment,
    deletePlaceholderTask,
    addTaskCreatedToBoard,
    crews,
    addNewCrew,
    refetchTasks,
  } = useOperationDespatchContext();

  const { currentUser } = useAppState();

  const onConfirmCancelTask = ({
    task,
    crew,
  }: BusEventPayloads["despatch:onSuccessCancelTask"]) => {
    if (!crew) {
      crew = crews.find((compare) => compare.tasks.some((value) => value.id === task.id));
    }

    if (task.id) {
      updateCrewTaskValueByTaskId(task.id, "status", TaskStatus.CANCELLED);
    }
  };

  const onAddTaskComment = ({
    comment,
    task,
  }: BusEventPayloads["despatch:onSuccessCreateComment"]) => {
    if (!comment) return;

    const { id, message, userId, createdAt } = comment;

    addTaskComment(task.id!, {
      id: id!,
      createdAt: createdAt!,
      message,
      userId,
      user: {
        id: currentUser?.id!,
        fullName: currentUser?.fullName!,
        picture: currentUser?.picture!,
      },
    });
  };

  const onDeleteTaskComment = ({
    commentId,
    task,
  }: BusEventPayloads["despatch:onSuccessDeleteComment"]) => {
    if (!commentId || !task || !task.id) return;

    deleteTaskComment(task.id, commentId);
  };

  const onAddNewStorageBoxToCrew = ({
    payload,
    storageBox,
    storageBoxDTO,
  }: BusEventPayloads["despatch:onSuccessCreateSingleUseBox"]) => {
    addNewStorageBoxToCrew(payload, {
      ...storageBox,
      storageBoxType: storageBoxDTO?.storageBoxType,
    });
  };

  const onTaskCreated = ({ task, taskDTO }: BusEventPayloads["despatch:onSuccessCreateTask"]) => {
    addTaskCreatedToBoard(task, taskDTO?.teamId);
  };

  const onPlaceholderTaskCreated = ({ task }: BusEventPayloads["despatch:onSuccessCreateTask"]) => {
    addTaskCreatedToBoard(task);
  };

  const onPlaceholderTaskDeleted = ({
    taskId,
  }: BusEventPayloads[BusEventNames.DESPATCH_ON_SUCCESS_DELETE_PLACEHOLDER_TASK]) => {
    if (!taskId) return;
    deletePlaceholderTask(taskId);
  };

  const onUpdateTaskTime = ({
    task,
  }: BusEventPayloads[BusEventNames.DESPATCH_ON_SUCCESS_UPDATE_TIME]) => {
    if (!task?.id) return;
    updateCrewTaskValueByTaskId(task?.id, "timeSlots", task?.timeSlots);
    updateCrewTaskValueByTaskId(task?.id, "startDate", task?.startDate);
    updateCrewTaskValueByTaskId(task?.id, "endDate", task?.endDate);
    updateCrewTaskValueByTaskId(task?.id, "period", task?.period);
  };

  const onTaskUpdated = ({ task }: BusEventPayloads["despatch:onSuccessUpdateTask"]) => {
    if (!task?.id) return;
    refetchTasks();
  };

  const onCrewCreated = ({ crew }: BusEventPayloads["despatch:onSuccessCreateCrew"]) => {
    addNewCrew(crew);
  };

  useEventBus(BusEventNames.DESPATCH_ON_SUCCESS_CANCEL_TASK, onConfirmCancelTask);

  useEventBus(BusEventNames.DESPATCH_ON_SUCCESS_CREATE_COMMENT, onAddTaskComment);

  useEventBus(BusEventNames.DESPATCH_ON_SUCCESS_DELETE_COMMENT, onDeleteTaskComment);

  useEventBus(
    BusEventNames.DESPATCH_ON_SUCCESS_CREATE_CREATE_SINGLE_USE_BOX,
    onAddNewStorageBoxToCrew
  );

  useEventBus(BusEventNames.DESPATCH_ON_SUCCESS_CREATE_TASK, onTaskCreated);

  useEventBus(BusEventNames.DESPATCH_ON_SUCCESS_CREATE_PLACEHOLDER_TASK, onPlaceholderTaskCreated);

  useEventBus(BusEventNames.DESPATCH_ON_SUCCESS_DELETE_PLACEHOLDER_TASK, onPlaceholderTaskDeleted);

  useEventBus(BusEventNames.DESPATCH_ON_SUCCESS_UPDATE_TASK, onTaskUpdated);

  useEventBus(BusEventNames.DESPATCH_ON_SUCCESS_UPDATE_TIME, onUpdateTaskTime);

  useEventBus(BusEventNames.DESPATCH_ON_SUCCESS_CREATE_CREW, onCrewCreated);
};
